import React, { useEffect, useState } from 'react';
import { Menu } from '../../ui/layout/Menu';
import useApi from '../../hooks/useApi';
import { userState } from '../../store';
import { useSnapshot } from 'valtio';
import { FooterCmp } from '../../ui/layout/Footer';
import { ApiError, User, UserWithRelations } from '../../@backend';
import { RiAiGenerate } from 'react-icons/ri';
import { GiLoad } from 'react-icons/gi';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../ui/ds/PageHeader';
import { Card } from '../../ui/ds/Card';
import { Input } from '../../ui/ds/Input';
import { Btn } from '../../ui/ds/Btn';
import { Badge } from '../../ui/ds/Badge';

export const MyProfile = () => {
    const userSnap  = useSnapshot(userState);
    const apiUrl    = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';
    const { backend } = useApi(true);
    const navigate  = useNavigate();

    const [user, setUser]                         = useState<UserWithRelations | null>(null);
    const [avatar, setAvatar]                     = useState<string | null>(null);
    const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
    const [isDeletingAccount, setIsDeletingAccount]   = useState(false);
    const [errorMessage, setErrorMessage]         = useState<string | null>(null);
    const [success, setSuccess]                   = useState(false);
    const [showAvatarModal, setShowAvatarModal]   = useState(false);
    const [showDeleteModal, setShowDeleteModal]   = useState(false);
    const [prompt, setPrompt]                     = useState('');

    const loadProfile = () => {
        backend.user.userControllerMyProfile()
            .then((res: UserWithRelations) => {
                setUser(res);
                if (res.avatar) setAvatar(`${apiUrl}/avatars/${res.avatar}`);
            })
            .catch((err: ApiError) => setErrorMessage(err.body?.error?.message ?? 'Chyba'));
    };

    useEffect(() => {
        if (userSnap.isLoggedIn) loadProfile();
    }, [userSnap.isLoggedIn]);

    if (!userSnap.isLoggedIn) {
        return (
            <><Menu /><div className="max-w-md mx-auto px-4 py-16 text-center"><p className="tb-h2 text-text-primary">Je vyžadováno přihlášení</p></div></>
        );
    }

    const generateAvatar = () => {
        setShowAvatarModal(false);
        setIsGeneratingAvatar(true);
        backend.user.userControllerGenerateAvatar(prompt)
            .then((res: any) => {
                setAvatar(res.avatarUrl);
                userState.userProfile = {
                    ...userState.userProfile,
                    avatar: `${userSnap.userProfile.id}.png?${Date.now()}`,
                };
                setIsGeneratingAvatar(false);
            })
            .catch((err: ApiError) => {
                setErrorMessage(err.body?.error?.message ?? 'Chyba při generování avataru');
                setIsGeneratingAvatar(false);
            });
    };

    const submitForm = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const userUpdated = {
            firstname: formData.get('firstname') as string,
            lastname:  formData.get('lastname')  as string,
            email:     formData.get('email')     as string,
            phone:     formData.get('phone')     as string,
        } as User;

        backend.user.userControllerUpdateMyProfile(userUpdated)
            .then(() => {
                setErrorMessage(null);
                setSuccess(true);
                userState.userProfile = { ...userState.userProfile, ...userUpdated };
            })
            .catch((err: ApiError) => {
                setSuccess(false);
                setErrorMessage(err.body?.error?.message ?? 'Chyba při ukládání');
            });
    };

    const clearUserSession = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('tipbuddy-user');
        userState.isLoggedIn  = false;
        userState.token       = null;
        userState.userProfile = {} as any;
    };

    const deleteAccount = () => {
        setIsDeletingAccount(true);
        setErrorMessage(null);
        backend.user.userControllerDeleteMyAccount()
            .then(() => {
                setShowDeleteModal(false);
                clearUserSession();
                navigate('/');
            })
            .catch((err: ApiError) => {
                setErrorMessage(err.body?.error?.message ?? 'Účet se nepodařilo smazat.');
                setIsDeletingAccount(false);
            });
    };

    return (
        <>
            <Menu />
            <PageHeader title="Můj profil" subtitle="Správa účtu a nastavení" />

            <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 flex flex-col gap-4">
                {/* Feedback banners */}
                {errorMessage && (
                    <div className="flex items-center gap-3 bg-semantic-danger/10 border border-semantic-danger/20 rounded-content px-4 py-3">
                        <Badge variant="danger">Chyba</Badge>
                        <span className="tb-body text-semantic-danger">{errorMessage}</span>
                    </div>
                )}
                {success && (
                    <div className="flex items-center gap-3 bg-semantic-success/10 border border-semantic-success/20 rounded-content px-4 py-3">
                        <Badge variant="success">Uloženo</Badge>
                        <span className="tb-body text-semantic-success">Změny byly úspěšně uloženy.</span>
                    </div>
                )}

                {user && (
                    <>
                        {/* Avatar + form */}
                        <Card>
                            <div className="flex flex-col md:flex-row gap-6">
                                {/* Avatar */}
                                <div className="flex flex-col items-center gap-4 flex-shrink-0">
                                    <div className="w-28 h-28 rounded-full overflow-hidden bg-border-neutral flex items-center justify-center">
                                        {avatar ? (
                                            <img src={avatar} alt="avatar" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-[28px] font-black text-text-muted font-nunito">
                                                {user.firstname?.charAt(0)}{user.lastname?.charAt(0)}
                                            </span>
                                        )}
                                    </div>
                                    <Btn
                                        variant="orange"
                                        size="sm"
                                        onClick={() => setShowAvatarModal(true)}
                                        disabled={isGeneratingAvatar}
                                        icon={isGeneratingAvatar ? <GiLoad className="animate-spin" /> : <RiAiGenerate />}
                                    >
                                        {isGeneratingAvatar ? 'Generuji...' : 'Generovat avatar'}
                                    </Btn>
                                </div>

                                {/* Form */}
                                <form onSubmit={submitForm} className="flex-1 flex flex-col gap-0">
                                    <Input
                                        label="Jméno"
                                        name="firstname"
                                        type="text"
                                        defaultValue={user.firstname ?? ''}
                                    />
                                    <Input
                                        label="Příjmení"
                                        name="lastname"
                                        type="text"
                                        defaultValue={user.lastname ?? ''}
                                    />
                                    <Input
                                        label="Email"
                                        name="email"
                                        type="email"
                                        defaultValue={user.email ?? ''}
                                    />
                                    <Input
                                        label="Telefon"
                                        name="phone"
                                        type="tel"
                                        defaultValue={user.phone ?? ''}
                                    />
                                    <Btn type="submit" variant="primary" fullWidth>
                                        Uložit změny
                                    </Btn>
                                </form>
                            </div>
                        </Card>

                        {/* Delete account */}
                        <Card>
                            <h2 className="tb-h3 text-semantic-danger mb-1">Smazání účtu</h2>
                            <p className="tb-body text-text-muted mb-4">
                                Smazání účtu je nevratné. Po potvrzení budete odhlášeni a všechna vaše data budou vymazána.
                            </p>
                            <Btn
                                variant="danger"
                                size="sm"
                                onClick={() => setShowDeleteModal(true)}
                                disabled={isDeletingAccount}
                            >
                                Smazat účet
                            </Btn>
                        </Card>
                    </>
                )}
            </div>

            <FooterCmp />

            {/* Avatar generation modal */}
            {showAvatarModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setShowAvatarModal(false)}>
                    <div className="bg-bg-card rounded-hero shadow-card w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                        <h2 className="tb-h2 text-text-primary mb-4">Generovat avatar</h2>
                        <Input
                            label="Popište sebe v angličtině"
                            placeholder="e.g. young man with glasses and dark hair"
                            value={prompt}
                            onChange={e => setPrompt(e.target.value)}
                        />
                        <div className="flex gap-3 mt-2">
                            <Btn variant="ghost" size="sm" fullWidth onClick={() => setShowAvatarModal(false)}>
                                Zrušit
                            </Btn>
                            <Btn variant="orange" size="sm" fullWidth onClick={generateAvatar} disabled={!prompt.trim()}>
                                Generovat
                            </Btn>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete account modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => !isDeletingAccount && setShowDeleteModal(false)}>
                    <div className="bg-bg-card rounded-hero shadow-card w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                        <h2 className="tb-h2 text-semantic-danger mb-3">Smazat účet</h2>
                        <p className="tb-body text-text-primary mb-2">Opravdu chcete smazat svůj účet? Tato akce je nevratná.</p>
                        <p className="tb-caption mb-6">Po smazání budete automaticky odhlášeni.</p>
                        <div className="flex gap-3">
                            <Btn
                                variant="ghost"
                                size="sm"
                                fullWidth
                                onClick={() => setShowDeleteModal(false)}
                                disabled={isDeletingAccount}
                            >
                                Zrušit
                            </Btn>
                            <Btn
                                variant="danger"
                                size="sm"
                                fullWidth
                                onClick={deleteAccount}
                                disabled={isDeletingAccount}
                            >
                                {isDeletingAccount ? 'Mažu...' : 'Ano, smazat'}
                            </Btn>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
